/**
 * Pure domain logic for rack metric computations and validation helpers.
 * Covers: usedU, fillPct (capped 0..100), totalWeight, totalPower, zero-U detection,
 * reservation collision detection, and unified rack capacity metrics.
 */

import { normalizeMountingPosition } from './halfDepth.logic.ts';

/**
 * Determines whether an equipment item is zero-U (mounted in vertical cable organizers / PDU slots),
 * and therefore does not consume horizontal rack units.
 */
export function isZeroUEquipment(eq: any): boolean {
  if (!eq) return false;
  if (eq.is_zero_u) return true;
  const mount = normalizeMountingPosition(eq.mounting_position);
  if (mount === 'zero_u') return true;
  if (eq.u_position !== undefined && eq.u_position !== null && !isNaN(Number(eq.u_position))) {
    if (Number(eq.u_position) <= 0) return true;
  }
  return false;
}

/**
 * Computes the number of physically occupied horizontal rack units (1-indexed).
 * Correctly excludes zero-U equipment, unpositioned items, and deduplicates multi-device/half-depth slots.
 */
export function calculateUsedU(equipmentList: any[] | null | undefined, totalU?: number): number {
  const maxU = totalU !== undefined && totalU !== null ? Math.floor(Number(totalU)) : 42;
  return calculateRackMetrics(equipmentList || [], [], maxU).occupiedU;
}

/**
 * Computes rack fill percentage (0..100%).
 * Capped at 100 to prevent overflow in progress indicators when rack is over-subscribed.
 * Returns 0 if totalU <= 0.
 */
export function calculateFillPct(usedU: number, totalU?: number): number {
  const slots = totalU !== undefined && totalU !== null ? Math.floor(Number(totalU)) : 42;
  if (slots <= 0) return 0;
  const raw = Math.round((Math.max(0, usedU) / slots) * 100);
  return Math.min(100, Math.max(0, raw));
}

/**
 * Sums equipment weight in kg, safely handling null/undefined/string values from NetBox
 * and clamping negative inputs.
 */
export function calculateTotalWeight(equipmentList: any[]): number {
  return (equipmentList || []).reduce((sum, eq) => {
    const w = Number(eq?.weight_kg) || 0;
    return sum + Math.max(0, w);
  }, 0);
}

/**
 * Sums equipment power in watts, safely handling aliases (power_w, power_watts, rated_power_w)
 * from NetBox and clamping negative values.
 */
export function calculateTotalPower(equipmentList: any[]): number {
  return (equipmentList || []).reduce((sum, eq) => {
    const raw = eq?.power_w ?? eq?.power_watts ?? eq?.rated_power_w ?? 0;
    const p = Number(raw) || 0;
    return sum + Math.max(0, p);
  }, 0);
}

/**
 * Checks whether candidate equipment placement overlaps with any existing rack reservation.
 * Zero-U items do not conflict with horizontal slot reservations.
 * Returns a warning string describing the reservation overlap, or null if clear.
 */
export function checkReservationWarning(
  candidate: { u_position?: number; u_height?: number; mounting_position?: string; is_zero_u?: boolean },
  reservations: any[]
): string | null {
  if (!reservations || reservations.length === 0) return null;
  if (!candidate || isZeroUEquipment(candidate)) return null;
  if (candidate.u_position === undefined || candidate.u_position === null || isNaN(Number(candidate.u_position))) return null;

  const candU = Math.floor(Number(candidate.u_position));
  const candH = Math.max(1, Math.floor(Number(candidate.u_height) || 1));
  const candEnd = candU + candH - 1;
  const candMount = normalizeMountingPosition(candidate.mounting_position);

  for (const res of reservations) {
    const resStart = Math.floor(Number(res.u_start));
    const resH = Math.max(1, Math.floor(Number(res.u_height) || 1));
    const resEnd = resStart + resH - 1;

    const overlap = Math.max(candU, resStart) <= Math.min(candEnd, resEnd);
    if (!overlap) continue;

    const resFace = (res.face || 'both').toLowerCase();
    let faceConflict = false;
    if (resFace === 'both' || candMount === 'full_depth') {
      faceConflict = true;
    } else if (resFace === 'front' && candMount === 'front') {
      faceConflict = true;
    } else if (resFace === 'rear' && candMount === 'rear') {
      faceConflict = true;
    }

    if (faceConflict) {
      const oStart = Math.max(candU, resStart);
      const oEnd = Math.min(candEnd, resEnd);
      const range = oStart === oEnd ? `U${oStart}` : `U${oStart}..U${oEnd}`;
      return `Слот ${range} зарезервирован под '${res.tenant || res.description || 'бронь'}'`;
    }
  }
  return null;
}

export interface RackMetricsSummary {
  totalU: number;
  occupiedU: number;
  reservedU: number;
  availableU: number;
  utilizationPct: number;
  occupiedPct: number;
  reservedPct: number;
  availablePct: number;
}

/**
 * Calculates unified 3-way space distribution (Occupied vs Reserved vs Available)
 * and utilization metrics across the rack.
 */
export function calculateRackMetrics(
  equipmentList: any[] | null | undefined,
  reservations: any[] | null | undefined = [],
  totalU: number = 42
): RackMetricsSummary {
  const maxU = Math.max(0, Math.floor(totalU));
  const occupiedSlots = new Set<number>();
  const reservedSlots = new Set<number>();

  for (const eq of equipmentList || []) {
    if (!eq || isZeroUEquipment(eq)) continue;
    if (eq.u_position === undefined || eq.u_position === null || isNaN(Number(eq.u_position))) continue;
    const start = Math.floor(Number(eq.u_position));
    if (start <= 0) continue;
    const h = Math.max(1, Math.floor(Number(eq.u_height) || 1));
    const end = Math.min(maxU, start + h - 1);
    for (let u = Math.max(1, start); u <= end; u++) {
      occupiedSlots.add(u);
    }
  }

  for (const res of reservations || []) {
    if (!res) continue;
    const start = Math.floor(Number(res.u_start));
    if (start <= 0 || isNaN(start)) continue;
    const h = Math.max(1, Math.floor(Number(res.u_height) || 1));
    const end = Math.min(maxU, start + h - 1);
    for (let u = Math.max(1, start); u <= end; u++) {
      if (!occupiedSlots.has(u)) {
        reservedSlots.add(u);
      }
    }
  }

  const occupiedU = occupiedSlots.size;
  const reservedU = reservedSlots.size;
  const availableU = Math.max(0, maxU - occupiedU - reservedU);

  const utilizationPct = maxU > 0 ? Math.min(100, Math.round(((occupiedU + reservedU) / maxU) * 100)) : 0;
  const occupiedPct = maxU > 0 ? Math.min(100, Math.round((occupiedU / maxU) * 100)) : 0;
  const reservedPct = maxU > 0 ? Math.min(100, Math.round((reservedU / maxU) * 100)) : 0;
  const availablePct = Math.max(0, 100 - utilizationPct);

  return {
    totalU: maxU,
    occupiedU,
    reservedU,
    availableU,
    utilizationPct,
    occupiedPct,
    reservedPct,
    availablePct,
  };
}
